import type {
  FeedbackContext,
  FeedbackSubmitResult,
  FeedbackType,
} from "@estelle/domain";

import { apiFetch } from "@/adapters/api/base";

/** What the dialog shows instead of the raw response — never a stack trace. */
export class FeedbackSubmitError extends Error {}

export interface SubmitFeedbackParams {
  type: FeedbackType;
  note: string;
  context: FeedbackContext;
  /** Null when the reporter dropped it, or capture failed outright. */
  screenshot: Blob | null;
}

/**
 * `POST /api/feedback`, multipart so the screenshot goes as the `Blob` the
 * browser already holds rather than a third larger over the wire as base64.
 */
export async function submitFeedback(
  params: SubmitFeedbackParams,
): Promise<FeedbackSubmitResult> {
  const form = new FormData();
  form.set("type", params.type);
  form.set("note", params.note);
  form.set("context", JSON.stringify(params.context));
  if (params.screenshot) {
    form.set("screenshot", params.screenshot, "screenshot.png");
  }

  const response = await apiFetch("/api/feedback", {
    method: "POST",
    body: form,
  });

  if (!response.ok) {
    const body: unknown = await response.json().catch(() => null);
    const message =
      typeof body === "object" && body !== null && "error" in body
        ? String(body.error)
        : "Could not send feedback.";
    throw new FeedbackSubmitError(message);
  }

  return (await response.json()) as FeedbackSubmitResult;
}
